import type { RoutineClip, RoutineProgress } from "./routineTypes";
import { validateTrim } from "./routineTypes";

export const MAX_SOURCE_BYTES = 500 * 1024 * 1024;
export const MAX_ROUTINE_SECONDS = 20 * 60;
export const MAX_ROUTINE_CLIPS = 20;

export function clipArguments(
  clip: Pick<RoutineClip, "start" | "end">,
  output: string,
  hasAudio: boolean,
): string[] {
  const duration = clip.end! - clip.start!;
  return [
    "-ss",
    String(clip.start),
    "-i",
    "input",
    ...(!hasAudio ? ["-f", "lavfi", "-i", "anullsrc=r=48000:cl=stereo"] : []),
    "-t",
    String(duration),
    "-map",
    "0:v:0",
    "-map",
    hasAudio ? "0:a:0" : "1:a:0",
    "-vf",
    "scale=1280:720:force_original_aspect_ratio=decrease:force_divisible_by=2,pad=1280:720:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=30,setpts=PTS-STARTPTS",
    "-af",
    "aresample=48000,asetpts=PTS-STARTPTS",
    "-c:v",
    "libx264",
    "-preset",
    "ultrafast",
    "-crf",
    "23",
    "-pix_fmt",
    "yuv420p",
    "-c:a",
    "aac",
    "-b:a",
    "192k",
    "-ar",
    "48000",
    "-ac",
    "2",
    "-threads",
    "1",
    "-y",
    output,
  ];
}

export async function renderRoutine(
  clips: RoutineClip[],
  sourceUrl: (clip: RoutineClip) => Promise<string>,
  progress: (value: RoutineProgress) => void,
  signal: AbortSignal,
): Promise<Blob> {
  if (!clips.length || clips.length > MAX_ROUTINE_CLIPS)
    throw new Error(`A routine needs 1–${MAX_ROUTINE_CLIPS} tutorials.`);
  let duration = 0;
  for (const clip of clips) {
    if (
      !clip.reviewed ||
      clip.start === null ||
      clip.end === null ||
      clip.duration === null ||
      validateTrim(clip.start, clip.end, clip.duration)
    )
      throw new Error("Every tutorial needs a valid selected range.");
    duration += clip.end - clip.start;
  }
  if (duration > MAX_ROUTINE_SECONDS)
    throw new Error(
      "Browser rendering currently supports routines up to 20 minutes.",
    );
  signal.throwIfAborted();
  progress({ stage: "Loading video tools" });
  const { FFmpeg } = await import("@ffmpeg/ffmpeg");
  const ffmpeg = new FFmpeg();
  const stop = () => ffmpeg.terminate();
  signal.addEventListener("abort", stop, { once: true });
  try {
    await ffmpeg.load({
      classWorkerURL: `${location.origin}/ffmpeg/worker.js`,
      coreURL: `${location.origin}/ffmpeg/ffmpeg-core.js`,
      wasmURL: `${location.origin}/ffmpeg/ffmpeg-core.wasm`,
    });
    for (let i = 0; i < clips.length; i++) {
      signal.throwIfAborted();
      const clip = clips[i]!;
      progress({ stage: `Preparing tutorial ${i + 1} of ${clips.length}` });
      const response = await fetch(await sourceUrl(clip), { signal });
      if (!response.ok)
        throw new Error(
          `Could not download ${clip.name}. Ask an admin to check its source MP4.`,
        );
      if (Number(response.headers.get("content-length")) > MAX_SOURCE_BYTES)
        throw new Error(`${clip.name} exceeds the 500 MB source limit.`);
      // Bound memory even when the host omits Content-Length.
      const reader = response.body?.getReader();
      if (!reader) throw new Error("The source video could not be downloaded.");
      const chunks: Uint8Array[] = [];
      let size = 0;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > MAX_SOURCE_BYTES) {
          await reader.cancel();
          throw new Error(`${clip.name} exceeds the 500 MB source limit.`);
        }
        chunks.push(value);
      }
      const data = new Uint8Array(size);
      let offset = 0;
      for (const part of chunks) {
        data.set(part, offset);
        offset += part.length;
      }
      chunks.length = 0;
      await ffmpeg.writeFile("input", data);
      // core 0.12.10 leaves ffprobe's return code at -1 even on success.
      // Validate its actual structured output instead of treating that as failure.
      await ffmpeg.ffprobe([
        "-v",
        "error",
        "-show_entries",
        "stream=codec_type:format=duration",
        "-of",
        "json",
        "input",
        "-o",
        "probe.json",
      ]);
      let probe;
      try {
        probe = JSON.parse(
          new TextDecoder().decode(
            (await ffmpeg.readFile("probe.json")) as Uint8Array,
          ),
        );
      } catch {
        throw new Error(
          `Could not read ${clip.name}. Upload a valid source video.`,
        );
      }
      if (
        !probe.streams?.some(
          (s: { codec_type: string }) => s.codec_type === "video",
        )
      )
        throw new Error(`${clip.name} has no video track.`);
      if (
        !Number.isFinite(Number(probe.format?.duration)) ||
        clip.end! > Number(probe.format?.duration) + 0.05
      )
        throw new Error(
          `${clip.name}'s trim extends beyond its source. Ask an admin to review it.`,
        );
      const onProgress = ({ time }: { time: number }) =>
        progress({
          stage: `Trimming tutorial ${i + 1} of ${clips.length} (approximate)`,
          percent: Math.max(
            0,
            Math.min(
              99,
              Math.round((time / 1e6 / (clip.end! - clip.start!)) * 100),
            ),
          ),
        });
      ffmpeg.on("progress", onProgress);
      const code = await ffmpeg.exec(
        clipArguments(
          clip,
          `clip-${i}.mp4`,
          probe.streams.some(
            (s: { codec_type: string }) => s.codec_type === "audio",
          ),
        ),
      );
      ffmpeg.off("progress", onProgress);
      if (code !== 0)
        throw new Error(
          `Could not trim ${clip.name}. Try a smaller source MP4 or a desktop browser.`,
        );
      await ffmpeg.deleteFile("input");
      await ffmpeg.deleteFile("probe.json");
    }
    signal.throwIfAborted();
    progress({ stage: "Combining your routine" });
    await ffmpeg.writeFile(
      "clips.txt",
      clips.map((_, i) => `file 'clip-${i}.mp4'`).join("\n"),
    );
    if (
      (await ffmpeg.exec([
        "-f",
        "concat",
        "-safe",
        "0",
        "-i",
        "clips.txt",
        "-c",
        "copy",
        "-movflags",
        "+faststart",
        "-y",
        "routine.mp4",
      ])) !== 0
    )
      throw new Error("Could not combine the routine. Please retry.");
    const result = (await ffmpeg.readFile("routine.mp4")) as Uint8Array;
    if (!result.byteLength || result.byteLength > MAX_SOURCE_BYTES)
      throw new Error("The generated video exceeds the 500 MB output limit.");
    return new Blob([result.slice().buffer], { type: "video/mp4" });
  } finally {
    signal.removeEventListener("abort", stop);
    ffmpeg.terminate();
  }
}
